import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { AppState } from '@store/store';

export type VideoState = {
  currentTime: number;
  totalDuration: number;
  videoWidth: number;
  videoHeight: number;
  aspectRatio: string | null;
  isMuted: boolean;
  isPaused: boolean;
  isAudioPresent: boolean;
  currentState: PlayerState;
  shouldPlayStop: boolean;
};

export enum PlayerState {
  PAUSED = 'PAUSED',
  PLAYING = 'PLAYING',
  SEEKING = 'SEEKING',
  DRAGGING = 'DRAGGING',
}

export interface ToolsPlayerState {
  videoState: VideoState;
}

const initialVideoState: ToolsPlayerState = {
  videoState: {
    currentTime: 0.0,
    totalDuration: 0.0,
    videoWidth: 0,
    videoHeight: 0,
    aspectRatio: null,
    isMuted: false,
    isPaused: true,
    isAudioPresent: true,
    currentState: PlayerState.PAUSED,
    shouldPlayStop: true,
  },
};

const PlayerSlice = createSlice({
  name: 'player',
  initialState: initialVideoState,
  reducers: {
    UPDATE_VIDEO_STATE: (state, action: PayloadAction<VideoState>) => {
      return {
        ...state,
        videoState: action.payload,
      };
    },
    UPDATE_VIDEO_CURRENT_TIME: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          currentTime: action.payload,
        },
      };
    },
    UPDATE_PLAYER_STATE: (state, action: PayloadAction<PlayerState>) => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          currentState: action.payload,
        },
      };
    },
    SHOULD_VIDEO_PLAYBACK_STOP: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          shouldPlayStop: action.payload,
        },
      };
    },
    PASUED_VIDEO: state => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          isPaused: true,
          currentState: PlayerState.PAUSED,
        },
      };
    },
    PLAY_VIDEO: state => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          isPaused: false,
          currentState: PlayerState.PLAYING,
        },
      };
    },
    RESET_PLAYER: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        videoState: {
          ...state.videoState,
          isPaused: true,
          currentTime: action.payload,
          currentState: PlayerState.PAUSED,
        },
      };
    },
    RESET_PLAYER_STATE: () => {
      return { ...initialVideoState };
    },
  },
});

const videoStateSelector = createDraftSafeSelector(
  (state: AppState) => state.player,
  state => state.videoState,
);

const currentTimeSelector = createDraftSafeSelector(
  (state: AppState) => state.player,
  state => state.videoState.currentTime,
);

const initialTimeSelector = createDraftSafeSelector(
  (state: AppState) => state.editor,
  state => state.currentBlock?.startFrameTime,
);

export const {
  PASUED_VIDEO,
  PLAY_VIDEO,
  UPDATE_PLAYER_STATE,
  UPDATE_VIDEO_CURRENT_TIME,
  UPDATE_VIDEO_STATE,
  RESET_PLAYER_STATE,
  RESET_PLAYER,
  SHOULD_VIDEO_PLAYBACK_STOP,
} = PlayerSlice.actions;

export { videoStateSelector, currentTimeSelector, initialTimeSelector };

export default PlayerSlice.reducer;
